import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useGetApiV1NotificationMobileAppNotificationidPreview } from '@/shared/api/generated/hooks/notificationMobileAppController/useGetApiV1NotificationMobileAppNotificationidPreview';
import { notificationWizardAtom } from '../store/atoms/notificationWizardAtom';
import { ENotificationMobileStatus } from '../types/notification';

/**
 * Hook para carregar os dados de uma notificação existente no wizard de edição.
 * Busca os dados via `/preview` e popula o `notificationWizardAtom` com as
 * informações da notificação, partindo do step 0.
 *
 * @param notificationId - ID da notificação a ser editada
 * @returns Estado de carregamento e erro da requisição
 *
 * @example
 * ```tsx
 * const { isLoading, isError } = useNotificationEdit(notificationId);
 * ```
 */
export function useNotificationEdit(notificationId: number) {
  const [, setState] = useAtom(notificationWizardAtom);

  const { data, isLoading, isError, refetch } =
    useGetApiV1NotificationMobileAppNotificationidPreview(notificationId);

  useEffect(() => {
    if (!data) return;

    const classroomIds = data.classStudentIds?.map(String) ?? [];
    const classroomNames: Record<string, string> = {};
    data.classStudentIds?.forEach((id, i) => {
      classroomNames[String(id)] = data.classStudentNames?.[i] ?? '';
    });

    const isScheduled = data.notificationType === ENotificationMobileStatus.Scheduled;

    setState({
      step: 0,
      notificationId,
      audience: {
        classroomIds,
        enrollmentStatuses: data.allowedEnrollmentStatus?.map(String) ?? [],
        classroomStudentCounts: {},
        classroomNames,
      },
      message: {
        title: data.title ?? '',
        body: data.message ?? '',
        url: data.urlExternal ?? '',
      },
      schedule: {
        mode: isScheduled ? 'scheduled' : 'now',
        scheduledAt: isScheduled ? (data.scheduledAtUtc ?? null) : null,
      },
      notificationType: data.notificationType,
    });
  }, [data, notificationId, setState]);

  return { isLoading, isError, refetch } as const;
}
